import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './SuperadminDashboard.css';

const BLANK_ONBOARD = {
  gymName: '', sport: 'Gymnastics', city: '', state: '', country: 'US',
  address: '', zip: '',
  adminName: '', adminEmail: '', adminPassword: ''
};

const PLAN_COLORS = { trial: '#e67e22', basic: '#2980b9', pro: '#27ae60', enterprise: '#8e44ad' };

function PlanBadge({ plan }) {
  return <span className="plan-badge" style={{ background: PLAN_COLORS[plan] || '#999' }}>{plan || 'trial'}</span>;
}

export default function SuperadminDashboard() {
  const toast = useToast();
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Global pose-estimator master switch.
  const [poseMode, setPoseMode] = useState('off'); // 'off' | 'nightly' | 'immediate'
  const [poseSaving, setPoseSaving] = useState(false);
  const [poseVisible, setPoseVisible] = useState(false); // show pose/landing charts in coach analytics
  const [poseVisSaving, setPoseVisSaving] = useState(false);

  // Platform / analysis health
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Quick onboard modal
  const [showOnboard, setShowOnboard] = useState(false);
  const [onboardForm, setOnboardForm] = useState(BLANK_ONBOARD);
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [onboardError, setOnboardError] = useState('');
  const [onboardSuccess, setOnboardSuccess] = useState('');

  // Gym detail panel
  const [selectedGym, setSelectedGym] = useState(null);
  const [gymUsers, setGymUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Add user to gym form (inside detail panel)
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ name: '', email: '', password: '', role: 'coach', phone: '' });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  const [addUserSuccess, setAddUserSuccess] = useState('');
  const [showAddPw, setShowAddPw] = useState(false);

  const fetchGyms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/superadmin/gyms`);
      setGyms(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load gyms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGyms(); }, [fetchGyms]);

  const fetchPoseSetting = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/superadmin/settings`);
      setPoseMode(data.pose_analysis_mode || (data.pose_analysis_enabled ? 'immediate' : 'off'));
      setPoseVisible(!!data.pose_analytics_visible);
    } catch { /* keep default */ }
  }, []);
  useEffect(() => { fetchPoseSetting(); }, [fetchPoseSetting]);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/superadmin/health`);
      setHealth(data);
    } catch { /* leave stale */ } finally { setHealthLoading(false); }
  }, []);
  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const retryFailed = async () => {
    setRetrying(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/admin/superadmin/retry-failed`);
      toast.success(`Re-queued ${data.retried} failed clip(s).`);
      fetchHealth();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Retry failed.');
    } finally { setRetrying(false); }
  };

  const setPoseVisibleRemote = async (visible) => {
    setPoseVisSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/admin/superadmin/settings`, { pose_analytics_visible: visible });
      setPoseVisible(visible);
      toast.success(visible
        ? 'Pose/landing charts are now VISIBLE in coach & athlete analytics.'
        : 'Pose/landing charts HIDDEN — analytics show attendance, meets & wellness only.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update setting.');
    } finally { setPoseVisSaving(false); }
  };

  const setPoseModeRemote = async (mode) => {
    if (mode === poseMode) return;
    setPoseSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/admin/superadmin/settings`, { pose_analysis_mode: mode });
      setPoseMode(mode);
      toast.success({
        off: 'Pose estimator OFF — record only, analyze later.',
        nightly: 'Pose estimator set to NIGHTLY — clips record now, analyze in the overnight batch.',
        immediate: 'Pose estimator ON — new videos analyze on upload.',
      }[mode]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update setting.');
    } finally {
      setPoseSaving(false);
    }
  };

  const fetchGymUsers = useCallback(async (gymId) => {
    setUsersLoading(true);
    setGymUsers([]);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/superadmin/gyms/${gymId}/users`);
      setGymUsers(data);
    } catch {}
    finally { setUsersLoading(false); }
  }, []);

  const openGym = (gym) => {
    setSelectedGym(gym);
    setShowAddUser(false);
    setAddUserError('');
    setAddUserSuccess('');
    fetchGymUsers(gym.id);
  };

  const closeGym = () => {
    setSelectedGym(null);
    setGymUsers([]);
    setShowAddUser(false);
  };

  const toggleGymStatus = async (gym) => {
    const action = gym.is_active ? 'suspend' : 'reactivate';
    if (!window.confirm(`${gym.is_active ? 'Suspend' : 'Reactivate'} "${gym.name}"?`)) return;
    try {
      await axios.put(`${API_BASE_URL}/api/admin/superadmin/gyms/${gym.id}`, { is_active: gym.is_active ? 0 : 1 });
      fetchGyms();
      if (selectedGym?.id === gym.id) setSelectedGym(g => ({ ...g, is_active: !g.is_active }));
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${action} gym`);
    }
  };

  const changePlan = async (gymId, plan) => {
    try {
      await axios.put(`${API_BASE_URL}/api/admin/superadmin/gyms/${gymId}`, { plan });
      fetchGyms();
      if (selectedGym?.id === gymId) setSelectedGym(g => ({ ...g, plan }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update plan');
    }
  };

  const handleOnboard = async (e) => {
    e.preventDefault();
    setOnboardError('');
    setOnboardSuccess('');
    setOnboardLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/admin/superadmin/onboard-gym`, {
        gymName: onboardForm.gymName,
        sport: onboardForm.sport,
        city: onboardForm.city,
        state: onboardForm.state,
        country: onboardForm.country,
        address: onboardForm.address,
        zip: onboardForm.zip,
        adminName: onboardForm.adminName,
        adminEmail: onboardForm.adminEmail,
        adminPassword: onboardForm.adminPassword
      });
      setOnboardSuccess(`✓ "${data.gym.name}" created! Admin: ${data.admin.email}`);
      setOnboardForm(BLANK_ONBOARD);
      fetchGyms();
    } catch (err) {
      setOnboardError(err.response?.data?.error || 'Failed to onboard gym');
    } finally {
      setOnboardLoading(false);
    }
  };

  const handleAddUserToGym = async (e) => {
    e.preventDefault();
    setAddUserError('');
    setAddUserSuccess('');
    setAddUserLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/admin/users`, {
        name: addUserForm.name,
        email: addUserForm.email,
        password: addUserForm.password,
        role: addUserForm.role,
        phone: addUserForm.phone || undefined,
        gymId: selectedGym.id
      });
      setAddUserSuccess(`✓ ${data.user.name} added as ${addUserForm.role}`);
      setAddUserForm({ name: '', email: '', password: '', role: 'coach', phone: '' });
      fetchGymUsers(selectedGym.id);
    } catch (err) {
      setAddUserError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setAddUserLoading(false);
    }
  };

  const totalActive = gyms.filter(g => g.is_active).length;
  const totalUsers = gyms.reduce((sum, g) => sum + (g.total_users || 0), 0);

  return (
    <div className="sad-root">
      {/* Header */}
      <div className="sad-header">
        <div>
          <h1>RMT Admin Console</h1>
          <p className="sad-sub">RockyMountain Technology — all gyms</p>
        </div>
        <button className="sad-btn-onboard" onClick={() => { setShowOnboard(true); setOnboardError(''); setOnboardSuccess(''); }}>
          + Quick Onboard Gym
        </button>
      </div>

      {/* Stats */}
      <div className="sad-stats">
        <div className="sad-stat"><span className="sad-stat-num">{gyms.length}</span><span>Total Gyms</span></div>
        <div className="sad-stat"><span className="sad-stat-num">{totalActive}</span><span>Active</span></div>
        <div className="sad-stat"><span className="sad-stat-num">{gyms.length - totalActive}</span><span>Suspended</span></div>
        <div className="sad-stat"><span className="sad-stat-num">{totalUsers}</span><span>Total Users</span></div>
      </div>

      {/* Platform + analysis health */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem', margin: '0 0 1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>🩺 Platform Health</div>
          <button onClick={fetchHealth} disabled={healthLoading}
            style={{ fontSize: '0.8rem', padding: '4px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>
            {healthLoading ? '…' : '↻ Refresh'}
          </button>
        </div>
        {!health ? (
          <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: 8 }}>Loading…</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
              {[
                { label: 'Redis + Worker', value: health.redis_ok ? 'Online' : 'DOWN', bad: !health.redis_ok, good: health.redis_ok },
                { label: 'Analysis mode', value: health.pose_mode },
                { label: 'Queued', value: health.queue?.waiting ?? '—' },
                { label: 'Processing', value: health.queue?.active ?? '—' },
                { label: 'Awaiting analysis', value: health.videos?.awaiting ?? '—' },
                { label: 'Failed', value: health.videos?.failed ?? 0, bad: (health.videos?.failed || 0) > 0 },
                { label: 'Analyzed', value: health.videos?.analyzed ?? 0 },
              ].map(t => (
                <div key={t.label} style={{
                  minWidth: 104, flex: '1 1 104px', padding: '0.6rem 0.75rem', borderRadius: 10,
                  background: t.bad ? '#fef2f2' : t.good ? '#ecfdf5' : '#f8fafc',
                  border: `1px solid ${t.bad ? '#fecaca' : t.good ? '#bbf7d0' : '#e5e7eb'}`,
                }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: t.bad ? '#b91c1c' : t.good ? '#15803d' : '#111827' }}>{t.value}</div>
                  <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>{t.label}</div>
                </div>
              ))}
            </div>
            {((health.videos?.failed || 0) > 0 || (health.queue?.failed || 0) > 0) && (
              <button onClick={retryFailed} disabled={retrying}
                style={{ marginTop: 12, fontSize: '0.85rem', fontWeight: 600, padding: '0.5rem 1rem', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: retrying ? 'wait' : 'pointer' }}>
                {retrying ? 'Re-queuing…' : `↻ Retry ${health.videos?.failed || 0} failed`}
              </button>
            )}
          </>
        )}
      </div>

      {/* Platform Settings — pose estimator mode */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem', margin: '0 0 1.25rem' }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>🦴 Pose Estimator</div>
        <div style={{ fontSize: '0.85rem', color: '#6b7280', margin: '4px 0 10px', maxWidth: 680 }}>
          {{
            off: 'OFF — record only. Clips are instantly viewable + annotatable; no analysis (analyze later).',
            nightly: 'NIGHTLY — clips record instantly during the day (no CPU load); the estimator runs them in an overnight batch (~2 AM).',
            immediate: 'IMMEDIATE — every upload runs full pose analysis right away (most CPU; slower to appear).',
          }[poseMode]}
        </div>
        <div role="radiogroup" aria-label="Pose estimator mode" style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 10, overflow: 'hidden' }}>
          {[
            { id: 'off', label: 'Off' },
            { id: 'nightly', label: '🌙 Nightly batch' },
            { id: 'immediate', label: 'Immediate' },
          ].map((opt, i) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={poseMode === opt.id}
              disabled={poseSaving}
              onClick={() => setPoseModeRemote(opt.id)}
              style={{
                padding: '0.5rem 1rem', border: 'none', cursor: poseSaving ? 'wait' : 'pointer',
                borderLeft: i === 0 ? 'none' : '1px solid #d1d5db',
                background: poseMode === opt.id ? '#2563eb' : '#fff',
                color: poseMode === opt.id ? '#fff' : '#374151',
                fontWeight: poseMode === opt.id ? 700 : 500, fontSize: '0.85rem',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Display flag — independent of the analysis pipeline above. Lets you run
            nightly analysis while keeping coach/athlete charts hidden during beta. */}
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: poseVisSaving ? 'wait' : 'pointer' }}>
            <input type="checkbox" checked={poseVisible} disabled={poseVisSaving}
              onChange={(e) => setPoseVisibleRemote(e.target.checked)} style={{ width: 18, height: 18 }} />
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>Show pose/landing charts in analytics</span>
          </label>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', margin: '4px 0 0 28px', maxWidth: 640 }}>
            {poseVisible
              ? 'ON — coach, athlete & parent Performance views show stick-rate / landing charts.'
              : 'OFF (beta) — Performance views lead with attendance, meets, reps & wellness; landing charts are hidden while the pose data & labels mature. Analysis can still run in the background.'}
          </div>
        </div>
      </div>

      {error && <div className="sad-error">{error}</div>}
      {loading && <div className="sad-loading">Loading gyms…</div>}

      {/* Gym List */}
      {!loading && (
        <div className="sad-gym-list">
          {gyms.map(gym => (
            <div key={gym.id} className={`sad-gym-card ${!gym.is_active ? 'suspended' : ''}`}>
              <div className="sad-gym-top">
                <div className="sad-gym-name">{gym.name}</div>
                <PlanBadge plan={gym.plan} />
                {!gym.is_active && <span className="sad-suspended-badge">Suspended</span>}
              </div>
              <div className="sad-gym-meta">
                {gym.sport && <span>{gym.sport}</span>}
                {(gym.city || gym.state) && <span>{[gym.city, gym.state].filter(Boolean).join(', ')}</span>}
                {gym.owner_email && <span className="sad-owner">Admin: {gym.owner_email}</span>}
              </div>
              <div className="sad-gym-counts">
                <span>{gym.athlete_count || 0} athletes</span>
                <span>{gym.coach_count || 0} coaches</span>
                <span>{gym.total_users || 0} total</span>
              </div>
              <div className="sad-gym-actions">
                <select
                  className="sad-plan-select"
                  value={gym.plan || 'trial'}
                  onChange={e => changePlan(gym.id, e.target.value)}
                >
                  {['trial', 'basic', 'pro', 'enterprise'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <button className="sad-btn-manage" onClick={() => openGym(gym)}>Manage</button>
                <button
                  className={`sad-btn-toggle ${gym.is_active ? 'suspend' : 'activate'}`}
                  onClick={() => toggleGymStatus(gym)}
                >
                  {gym.is_active ? 'Suspend' : 'Reactivate'}
                </button>
              </div>
            </div>
          ))}
          {gyms.length === 0 && !loading && <div className="sad-empty">No gyms yet. Use Quick Onboard to add one.</div>}
        </div>
      )}

      {/* ── Gym Detail Panel ── */}
      {selectedGym && (
        <div className="sad-panel-overlay" onClick={e => { if (e.target === e.currentTarget) closeGym(); }}>
          <div className="sad-panel">
            <div className="sad-panel-header">
              <div>
                <h2>{selectedGym.name}</h2>
                <div className="sad-panel-meta">
                  <PlanBadge plan={selectedGym.plan} />
                  {!selectedGym.is_active && <span className="sad-suspended-badge">Suspended</span>}
                  {[selectedGym.city, selectedGym.state].filter(Boolean).join(', ')}
                </div>
              </div>
              <button className="sad-panel-close" onClick={closeGym}>✕</button>
            </div>

            <div className="sad-panel-actions">
              <button className="sad-btn-add-user" onClick={() => { setShowAddUser(v => !v); setAddUserError(''); setAddUserSuccess(''); }}>
                {showAddUser ? 'Cancel' : '+ Add User to Gym'}
              </button>
            </div>

            {/* Add user sub-form */}
            {showAddUser && (
              <div className="sad-add-user-form">
                {addUserError && <div className="sad-form-error">{addUserError}</div>}
                {addUserSuccess && <div className="sad-form-success">{addUserSuccess}</div>}
                <form onSubmit={handleAddUserToGym}>
                  <div className="sad-form-row">
                    <input type="text" placeholder="Full Name *" required value={addUserForm.name}
                      onChange={e => setAddUserForm(f => ({ ...f, name: e.target.value }))} />
                    <select value={addUserForm.role} onChange={e => setAddUserForm(f => ({ ...f, role: e.target.value }))}>
                      {['athlete', 'parent', 'coach', 'admin'].map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="sad-form-row">
                    <input type="email" placeholder="Email *" required value={addUserForm.email}
                      onChange={e => setAddUserForm(f => ({ ...f, email: e.target.value }))} />
                    <input type="tel" placeholder="Phone (optional)" value={addUserForm.phone}
                      onChange={e => setAddUserForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div className="sad-form-row">
                    <div className="sad-pw-wrap">
                      <input type={showAddPw ? 'text' : 'password'} placeholder="Temp Password * (min 8)" required minLength={8}
                        value={addUserForm.password}
                        onChange={e => setAddUserForm(f => ({ ...f, password: e.target.value }))} />
                      <button type="button" onClick={() => setShowAddPw(v => !v)}>{showAddPw ? 'Hide' : 'Show'}</button>
                    </div>
                    <button type="submit" className="sad-btn-submit" disabled={addUserLoading}>
                      {addUserLoading ? 'Adding…' : 'Add User'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* User list */}
            <div className="sad-users-list">
              <h3>Users ({gymUsers.length})</h3>
              {usersLoading ? <div className="sad-loading">Loading users…</div> : (
                gymUsers.length === 0
                  ? <div className="sad-empty">No users in this gym yet.</div>
                  : <table className="sad-users-table">
                      <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th></tr></thead>
                      <tbody>
                        {gymUsers.map(u => (
                          <tr key={u.id} className={!u.is_active ? 'row-dim' : ''}>
                            <td>{u.name}</td>
                            <td className="sad-email">{u.email}</td>
                            <td><span className="sad-role-pill">{u.role}</span></td>
                            <td><span className={`sad-status ${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Suspended'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Quick Onboard Modal ── */}
      {showOnboard && (
        <div className="sad-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowOnboard(false); }}>
          <div className="sad-modal">
            <div className="sad-modal-header">
              <h2>Quick Onboard New Gym</h2>
              <button className="sad-panel-close" onClick={() => setShowOnboard(false)}>✕</button>
            </div>

            {onboardError && <div className="sad-form-error">{onboardError}</div>}
            {onboardSuccess && <div className="sad-form-success">{onboardSuccess}</div>}

            <form onSubmit={handleOnboard} className="sad-onboard-form">
              <fieldset>
                <legend>Gym Info</legend>
                <div className="sad-form-row">
                  <div className="sad-form-field">
                    <label>Gym Name *</label>
                    <input type="text" required value={onboardForm.gymName}
                      onChange={e => setOnboardForm(f => ({ ...f, gymName: e.target.value }))} placeholder="Rocky Mountain Elite" />
                  </div>
                  <div className="sad-form-field">
                    <label>Sport</label>
                    <input type="text" value={onboardForm.sport}
                      onChange={e => setOnboardForm(f => ({ ...f, sport: e.target.value }))} placeholder="Gymnastics" />
                  </div>
                </div>
                <div className="sad-form-row">
                  <div className="sad-form-field">
                    <label>City</label>
                    <input type="text" value={onboardForm.city}
                      onChange={e => setOnboardForm(f => ({ ...f, city: e.target.value }))} placeholder="Denver" />
                  </div>
                  <div className="sad-form-field sad-form-field--sm">
                    <label>State</label>
                    <input type="text" value={onboardForm.state} maxLength={2}
                      onChange={e => setOnboardForm(f => ({ ...f, state: e.target.value.toUpperCase() }))} placeholder="CO" />
                  </div>
                  <div className="sad-form-field sad-form-field--sm">
                    <label>Zip</label>
                    <input type="text" value={onboardForm.zip}
                      onChange={e => setOnboardForm(f => ({ ...f, zip: e.target.value }))} placeholder="80201" />
                  </div>
                </div>
                <div className="sad-form-row">
                  <div className="sad-form-field sad-form-field--full">
                    <label>Street Address</label>
                    <input type="text" value={onboardForm.address}
                      onChange={e => setOnboardForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Main St" />
                  </div>
                </div>
              </fieldset>

              <fieldset>
                <legend>Admin Account</legend>
                <div className="sad-form-row">
                  <div className="sad-form-field">
                    <label>Admin Name *</label>
                    <input type="text" required value={onboardForm.adminName}
                      onChange={e => setOnboardForm(f => ({ ...f, adminName: e.target.value }))} placeholder="Coach Kim" />
                  </div>
                  <div className="sad-form-field">
                    <label>Admin Email *</label>
                    <input type="email" required value={onboardForm.adminEmail}
                      onChange={e => setOnboardForm(f => ({ ...f, adminEmail: e.target.value }))} placeholder="kim@rockyelite.com" />
                  </div>
                </div>
                <div className="sad-form-row">
                  <div className="sad-form-field">
                    <label>Admin Password * (min 8 chars)</label>
                    <input type="password" required minLength={8} value={onboardForm.adminPassword}
                      onChange={e => setOnboardForm(f => ({ ...f, adminPassword: e.target.value }))} placeholder="Temporary password" />
                  </div>
                </div>
              </fieldset>

              <div className="sad-modal-footer">
                <button type="button" className="sad-btn-cancel" onClick={() => { setShowOnboard(false); setOnboardForm(BLANK_ONBOARD); }}>
                  Cancel
                </button>
                <button type="submit" className="sad-btn-create" disabled={onboardLoading}>
                  {onboardLoading ? 'Creating…' : 'Create Gym + Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
